import { Component, Host, Prop, State, Element, h } from '@stencil/core';
import { mapService } from '../../utils/services/mapService';
import { storeService } from '../../utils/services/storeService';
import { Store } from '../../models/stores/Store';
import { ComponentColors, defaultComponentColors, initColors } from '../../utils/constants/colors';
import { googleMapsLoader } from '../../utils/services/googleMapsLoader';
import { formSubmissionService } from '../../utils/services/formSubmissionService';
import { z } from 'zod';
import { ASL_DetailedViewProps } from '../aico-store-locator-detailed-view/aico-store-locator-detailed-view';
import { ASL_SearchBarProps } from '../aico-store-locator-search-bar/aico-store-locator-search-bar';
import { ASL_FilterDropDownProps } from '../aico-store-locator-filter-dropdown/aico-store-locator-filter-dropdown';
import { ASL_ListProps } from '../aico-store-locator-list/aico-store-locator-list';
import { ASL_MapProps } from '../aico-store-locator-map/aico-store-locator-map';
import { ASL_StoreInfoCardProps } from '../aico-store-locator-store-info-card/aico-store-locator-store-info-card';
import { ASL_MapInfoWindowProps } from '../aico-store-locator-map-info-window/aico-store-locator-map-info-window';
import { brandService } from '../../utils/services/brandService';
import { InitialLocationSource, locationService } from '../../utils/services/locationService';
import { translationService, TranslationsProps } from '../../utils/services/translationService';


export const ListPosition = z.enum(['left', 'right']);
export type ListPosition = z.infer<typeof ListPosition>;


export const ASL_ComponentProps = z.object({
  enableClustering: z.boolean().optional(),
  listPosition: ListPosition.optional(),
  mapCenterLat: z.number().optional(),
  mapCenterLng: z.number().optional(),
  enabledBrands: z.array(z.string()).optional(),
  componentColors: ComponentColors.optional(),
  initialLocationSource: InitialLocationSource.optional(),
  translationsProps: TranslationsProps.optional(),
});
export type ASL_ComponentProps = z.infer<typeof ASL_ComponentProps>;
export const Props = z.object({
  ASL_ComponentProps: ASL_ComponentProps.optional(),
  ASL_SearchBarProps: ASL_SearchBarProps.optional(),
  ASL_FilterDropDownProps: ASL_FilterDropDownProps.optional(),
  ASL_StoreInfoCardProps: ASL_StoreInfoCardProps.optional(),
  ASL_ListProps: ASL_ListProps.optional(),
  ASL_MapProps: ASL_MapProps.optional(),

  Mobile: z.object({
    ASL_SearchBarProps: ASL_SearchBarProps.optional(),
    ASL_FilterDropDownProps: ASL_FilterDropDownProps.optional(),
    ASL_StoreInfoCardProps: ASL_StoreInfoCardProps.optional(),
    ASL_ListProps: ASL_ListProps.optional(),
    ASL_MapProps: ASL_MapProps.optional(),

    List: z.object({
      ASL_FilterDropDownProps: ASL_FilterDropDownProps.optional(),
      ASL_SearchBarProps: ASL_SearchBarProps.optional(),
    }).optional(),

    Map: z.object({
      ASL_FilterDropDownProps: ASL_FilterDropDownProps.optional(),
      ASL_SearchBarProps: ASL_SearchBarProps.optional(),
    }).optional(),
  }).optional(),

  Desktop: z.object({
    ASL_SearchBarProps: ASL_SearchBarProps.optional(),
    ASL_FilterDropDownProps: ASL_FilterDropDownProps.optional(),
    ASL_StoreInfoCardProps: ASL_StoreInfoCardProps.optional(),
    ASL_ListProps: ASL_ListProps.optional(),
    ASL_MapProps: ASL_MapProps.optional(),
  }).optional(),

  ASL_DetailedViewProps: ASL_DetailedViewProps.optional(),
}).transform((props) => {
  if (props.ASL_StoreInfoCardProps) {
    if (!props.ASL_ListProps) {
      props.ASL_ListProps = ASL_ListProps.parse({});
    }
    if (!props.ASL_ListProps.ASL_StoreInfoCardProps) {
      props.ASL_ListProps.ASL_StoreInfoCardProps = props.ASL_StoreInfoCardProps;
    }

    if (!props.ASL_MapProps) {
      props.ASL_MapProps = ASL_MapProps.parse({});
    }
    if (!props.ASL_MapProps.ASL_MapInfoWindowProps) {
      props.ASL_MapProps.ASL_MapInfoWindowProps = ASL_MapInfoWindowProps.parse({});
    }
    if (!props.ASL_MapProps.ASL_MapInfoWindowProps.ASL_StoreInfoCardProps) {
      props.ASL_MapProps.ASL_MapInfoWindowProps.ASL_StoreInfoCardProps = props.ASL_StoreInfoCardProps;
    }

    if (!props.ASL_DetailedViewProps) {
      props.ASL_DetailedViewProps = ASL_DetailedViewProps.parse({});
    }
    if (!props.ASL_DetailedViewProps.ASL_StoreInfoCardProps) {
      props.ASL_DetailedViewProps.ASL_StoreInfoCardProps = props.ASL_StoreInfoCardProps;
    }
  }
  return props;
});
export type Props = z.infer<typeof Props>;

@Component({
  tag: 'aico-store-locator-component',
  styleUrl: 'aico-store-locator-component.css',
  shadow: true
})
export class AicoStoreLocatorComponent {
  /**
   * Enables clustering on the map
   */
  @Prop() enableClustering: boolean = false;

  /**
   * Determines the position of the list, 'left' or 'right'. Defaults to 'left'.
   */
  @Prop() listPosition: ListPosition = 'left';

  /**
   * The initial center of the map.
   */
  @Prop() mapCenterLat: number = 47.3769;
  @Prop() mapCenterLng: number = 8.5417;
  @Prop() mapCenter: google.maps.LatLngLiteral = { lat: this.mapCenterLat, lng: this.mapCenterLng };
  @Prop() enabledBrands: string[] = [];
  @Prop() initialLocationSource: InitialLocationSource = 'browser';

  @Prop() componentColors: ComponentColors = defaultComponentColors;

  @Prop() defaultLocale: string = 'en';
  @Prop() locale?: string;
  @Prop() localeQueryParam?: string;
  @Prop() localeLocalStorageKey?: string;



  @Prop() googleMapsApiKey: string = '';
  @Prop() aicoApiUrl: string = '';
  @Prop() aicoEmailApiUrl: string = '';
  @Prop() aicoApiKey: string = '';
  @Prop() mapId!: string;

  @Prop() props?: Props | string;
  private propsObj?: Props;


  @State() isMobile: boolean = false;
  @State() isMapOpen: boolean = false;
  @State() isLoadingLocation: boolean = false;

  private mediaQuery!: MediaQueryList;
  private mediaQueryListener!: (e: MediaQueryListEvent) => void;
  private unsubscribeController!: AbortController;

  @Element() el!: HTMLElement;
  async componentWillLoad() {
    if (this.props && typeof this.props === 'string') {
      try {
        this.props = JSON.parse(this.props);
      } catch (error) {
        console.error('Invalid JSON provided in props:', error);
        this.props = undefined;
      }
    }
    if (this.props) {
      this.propsObj = Props.parse(this.props);
      this.enableClustering = this.propsObj.ASL_ComponentProps?.enableClustering ?? this.enableClustering;
      this.listPosition = this.propsObj.ASL_ComponentProps?.listPosition ?? this.listPosition;
      if (this.propsObj.ASL_ComponentProps?.mapCenterLat && this.propsObj.ASL_ComponentProps?.mapCenterLng) {
        this.mapCenter = {
          lat: this.propsObj.ASL_ComponentProps.mapCenterLat,
          lng: this.propsObj.ASL_ComponentProps.mapCenterLng,
        };
      }
      this.enabledBrands = this.propsObj.ASL_ComponentProps?.enabledBrands ?? this.enabledBrands;
      this.initialLocationSource = this.propsObj.ASL_ComponentProps?.initialLocationSource ?? this.initialLocationSource;

      this.componentColors = this.propsObj.ASL_ComponentProps?.componentColors ?? this.componentColors;

      this.locale = this.propsObj.ASL_ComponentProps?.translationsProps?.locale ?? this.locale;
      this.defaultLocale = this.propsObj.ASL_ComponentProps?.translationsProps?.defaultLocale ?? this.defaultLocale;
      this.localeQueryParam = this.propsObj.ASL_ComponentProps?.translationsProps?.queryParam ?? this.localeQueryParam;
      this.localeLocalStorageKey = this.propsObj.ASL_ComponentProps?.translationsProps?.localStorageKey ?? this.localeLocalStorageKey;
    }



    this.unsubscribeController?.abort();
    this.unsubscribeController = new AbortController();

    translationService.init({
      locale: this.locale,
      defaultLocale: this.defaultLocale,
      queryParam: this.localeQueryParam,
      localStorageKey: this.localeLocalStorageKey,
    });

    brandService.init(this.enabledBrands);
    storeService.init(this.aicoApiUrl, this.aicoApiKey, this.enabledBrands);
    formSubmissionService.init(this.aicoEmailApiUrl, this.aicoApiKey);
    googleMapsLoader.init(this.googleMapsApiKey);


    await translationService.initComponentLoad('aico-store-locator-component');
    console.log('[aico-store-locator] Translations initialized for aico-store-locator-component');
    console.log(translationService.translations);
    this.componentColors = ComponentColors.parse(this.componentColors);
    initColors(this.el, this.componentColors);

    const sharedStoreId = this.getSharedStoreId();

    if (sharedStoreId) {
      storeService.fetchStore(Number(sharedStoreId)).then(
        (store: Store) => {
          storeService.setSelectedStore(store);
          mapService.setCenter({ lat: store.latitude, lng: store.longitude });
        });
    }
    else {
      mapService.setCenter(this.mapCenter);
      if (this.initialLocationSource !== 'no') {
        this.isLoadingLocation = true;
        locationService.getLocation(this.initialLocationSource).then((location) => {
          if (location) {
            this.mapCenter = { lat: location.lat, lng: location.lng };
            mapService.setCenter(this.mapCenter);
          } else {
            console.warn('[aico-store-locator] Initial location not obtained from chosen source, falling back to configured map center.');
          }
        }).catch((error) => {
          console.error('[aico-store-locator] Error getting initial location:', error);
        }).finally(() => {
          // mapService.setCenter(this.mapCenter);
          this.isLoadingLocation = false;
        });
      }
    }

    this.mediaQuery = window.matchMedia('(max-width: 768px)');
    this.isMobile = this.mediaQuery.matches;
    this.mediaQueryListener = (e: MediaQueryListEvent) => {
      this.isMobile = e.matches;
    };
    this.mediaQuery.addEventListener('change', this.mediaQueryListener, { signal: this.unsubscribeController.signal });
  }

  private t(key:string, locale?:string): string {
    return translationService.getTranslation('aico-store-locator-component', key, locale);
  }

  private getSharedStoreId(): string | null {
    const url: URL = new URL(window.location.href);
    return url.searchParams.get('asl_s_id');
  }

  disconnectedCallback() {
    this.unsubscribeController?.abort();
  }

  private openMap() {
    this.isMapOpen = true;
  }

  private closeMap() {
    this.isMapOpen = false;
  }



  render() {
    return (
      <Host>
        {this.isMobile ? (
          // MOBILE LAYOUT
          <div class="bg-[--background-light-color]">
            {/* List view */}
            <div class={`${this.isMapOpen ? "hidden" : ""}`}>
              <div class="flex items-center gap-2 p-2">
                <div class="flex-1">
                  <aico-store-locator-search-bar disableLocationPin={true}
                    props={
                      this.propsObj?.Mobile?.List?.ASL_SearchBarProps ||
                      this.propsObj?.Mobile?.ASL_SearchBarProps ||
                      this.propsObj?.ASL_SearchBarProps} />
                </div>
                <div class="flex-shrink-0">
                  <button
                    class="inline-flex items-center gap-2 bg-[--primary-button-background-color] hover:bg-[--primary-button-hover-color] px-4 py-2 rounded-full text-[--primary-button-text-color] transition-colors"
                    onClick={() => this.openMap()}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="w-5 h-5"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <path d="M3 6l6-3 6 3 6-3v15l-6 3-6-3-6 3V6z" />
                      <path d="M9 3v15" />
                      <path d="M15 6v15" />
                    </svg>
                    <span class="font-medium text-sm">{this.t('Map')}</span>
                  </button>
                </div>
              </div>
              <aico-store-locator-filter-dropdown dropdownPosition="left"
                props={
                  this.propsObj?.Mobile?.List?.ASL_FilterDropDownProps ||
                  this.propsObj?.Mobile?.ASL_FilterDropDownProps ||
                  this.propsObj?.ASL_FilterDropDownProps} />
              <div class="h-[calc(100vh-140px)] overflow-auto">
                <aico-store-locator-list isAttached={false} props={
                  this.propsObj?.Mobile?.ASL_ListProps ||
                  this.propsObj?.ASL_ListProps
                } />
              </div>
            </div>

            {/* Map view */}
            <div class={`${this.isMapOpen ? "" : "hidden"} relative h-screen`}>
              <aico-store-locator-map
                enableClustering={this.enableClustering}
                isAttached={false}
                mapId={this.mapId}
                props={this.propsObj?.Mobile?.ASL_MapProps || this.propsObj?.ASL_MapProps}
                class="w-full h-full"
              />
              <div class="top-0 right-0 left-0 z-50 absolute p-4">
                {/* First row: Close button and search bar */}
                <div class="flex items-center gap-2 mb-2">
                  <button
                    class="bg-[--secondary-button-background-color] hover:bg-[--secondary-button-hover-color] p-2 rounded-full text-[--secondary-button-text-color]"
                    onClick={() => this.closeMap()}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="w-6 h-6"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                  <div class="flex-1">
                    <aico-store-locator-search-bar disableLocationPin={false}
                      props={
                        this.propsObj?.Mobile?.Map?.ASL_SearchBarProps ||
                        this.propsObj?.Mobile?.ASL_SearchBarProps ||
                        this.propsObj?.ASL_SearchBarProps} />
                  </div>
                </div>
                {/* Second row: Filter dropdown anchored to right */}
                <div class="flex justify-end">
                  <aico-store-locator-filter-dropdown buttonAppearance="bubble" dropdownPosition="right"
                    props={
                      this.propsObj?.Mobile?.List?.ASL_FilterDropDownProps ||
                      this.propsObj?.Mobile?.ASL_FilterDropDownProps ||
                      this.propsObj?.ASL_FilterDropDownProps} />
                </div>
              </div>
            </div>
          </div>
        ) : (
          // DESKTOP LAYOUT with max height and only the list scrolling
          <div class="bg-[--background-light-color] h-screen max-h-[800px]">
            <div class="flex h-full">
              {this.listPosition != 'left' && (<div class="flex-grow">
                <aico-store-locator-map
                  enableClustering={this.enableClustering}
                  isAttached={true}
                  mapId={this.mapId}
                  props={this.propsObj?.Desktop?.ASL_MapProps || this.propsObj?.ASL_MapProps}
                  class="w-full h-full"
                />
              </div>)}
              <div class="flex flex-col flex-1 max-w-96">
                <aico-store-locator-search-bar
                  class="p-2"
                  disableLocationPin={false}
                  props={
                    this.propsObj?.Desktop?.ASL_SearchBarProps ||
                    this.propsObj?.ASL_SearchBarProps} />

                <aico-store-locator-filter-dropdown
                  dropdownPosition="left"
                  class="py-2"
                  props={
                    this.propsObj?.Desktop?.ASL_FilterDropDownProps ||
                    this.propsObj?.ASL_FilterDropDownProps} />

                <div class="flex-1 overflow-auto">
                  <aico-store-locator-list isAttached={true} props={
                    this.propsObj?.Desktop?.ASL_ListProps ||
                    this.propsObj?.ASL_ListProps
                  } />
                </div>
              </div>
              {this.listPosition == 'left' && (<div class="flex-grow">
                <aico-store-locator-map
                  enableClustering={this.enableClustering}
                  isAttached={true}
                  mapId={this.mapId}
                  class="w-full h-full"
                />
              </div>)}
            </div>
          </div>
        )}
        <aico-store-locator-detailed-view
          mapId={this.mapId}
          openFrom={'left'}
          props={this.propsObj?.ASL_DetailedViewProps} />
      </Host>
    );
  }
}
